import { unifiedClient } from '@/lib/unified-fetch';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

// Types for chat system
export interface ChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  isStreaming?: boolean;
}

export interface ChatConversation {
  id: string; // Now always a UUID string
  messages: ChatMessage[];
  title?: string;
  lastActivity: Date;
}

export interface SendMessageRequest {
  query: string;
  conversationId: string; // Now a UUID string
}

// SSE Response types
export interface SSEMessageData {
  message: {
    type: 'text';
    text: string;
  };
  conversation_id: string;
  answer_id: string;
}

// React Query keys
export const chatKeys = {
  conversations: ['chat-conversations'] as const,
  conversation: (id: string) => ['chat-conversation', id] as const,
};

/**
 * Generate a new conversation ID using UUID v4
 */
export const generateConversationId = (): string => {
  return uuidv4();
};

/**
 * Hook to send a message and receive streaming response
 * Note: This hook only initiates the request. SSE streaming is handled separately.
 */
export const useSendMessage = () => {
  return useMutation<void, Error, SendMessageRequest>({
    mutationFn: async ({ query, conversationId }: SendMessageRequest) => {
      // The actual API call and SSE streaming is handled by the streaming utility
      // This mutation is mainly for React Query state management
      return Promise.resolve();
    },
  });
};



/**
 * Generate a unique message ID
 */
export const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Generate conversation title from first message
 */
export const generateConversationTitle = (firstMessage: string): string => {
  // Take first 50 characters and add ellipsis if longer
  const maxLength = 50;
  if (firstMessage.length <= maxLength) {
    return firstMessage;
  }
  return firstMessage.substring(0, maxLength).trim() + '...';
};
